import { useEffect, useState } from "react";

import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDocumentViewport } from "@/features/document-viewport/context";
import type { MeasurementScale } from "@/hooks/useMeasurementScale";
import { formatMeasurementHeight, type Point } from "@/lib/geometry";
import { getUserFacingErrorMessage } from "@/lib/user-facing-errors";
import { TOOL_IDS } from "@/viewport-tools/measurement";
import { Check, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import {
  formatDisplayedMeasurement,
  getEditableValueLabel,
  getScreenBoundsForGeometry,
  measurementRequiresHeight,
  shouldShowMeasurementLeader,
} from "@/viewport-tools/measurement/overlay/measurement-overlay-helpers";
import type {
  DraftMeasurementState,
  MeasurementEditDraft,
  MeasurementAnnotation,
  MeasurementOverlayRecord,
  MeasurementTagOption,
  PolygonGeometry,
} from "@/viewport-tools/measurement/overlay/measurement-overlay-types";
import { MEASUREMENT_GEOMETRY_TYPES } from "@/viewport-tools/measurement/overlay/measurement-overlay-types";

const UNASSIGNED_TAG_VALUE = "__unassigned_tag__";
const CONTEXT_MENU_ITEM_CLASS =
  "flex w-full rounded-sm px-2 py-1 text-left text-xs leading-tight hover:bg-muted";

function isPointEditableMeasurement(
  measurement: MeasurementOverlayRecord | null
): measurement is MeasurementOverlayRecord & { geometry: PolygonGeometry } {
  return (
    !!measurement &&
    measurement.geometry.type === MEASUREMENT_GEOMETRY_TYPES.poly &&
    (measurement.tool === TOOL_IDS.polyline || measurement.tool === TOOL_IDS.polygon)
  );
}

function canInsertPointOnMeasurement(measurement: MeasurementOverlayRecord | null) {
  return isPointEditableMeasurement(measurement) && measurement.geometry.vertices.length >= 2;
}

function canRemovePointFromMeasurement(measurement: MeasurementOverlayRecord | null) {
  if (!isPointEditableMeasurement(measurement)) {
    return false;
  }
  const minVertexCount = measurement.tool === TOOL_IDS.polygon ? 3 : 2;
  return measurement.geometry.vertices.length > minVertexCount;
}

function getContextSelectionIds(
  measurement: MeasurementOverlayRecord | null,
  selectedMeasurementIds: Id<"takeoffPilotMeasurements">[]
) {
  if (!measurement) {
    return [];
  }
  return selectedMeasurementIds.includes(measurement._id)
    ? selectedMeasurementIds
    : [measurement._id];
}

export function DraftMeasurementCard({
  draft,
  draftTagId,
  tags,
  onDraftTagChange,
  onSaveDraftMeasurement,
  onCancelDraftMeasurement,
  isSavingDraftMeasurement,
}: {
  draft: DraftMeasurementState;
  draftTagId: Id<"takeoffPilotExtractedTags"> | null;
  tags: MeasurementTagOption[];
  onDraftTagChange: (tagId: Id<"takeoffPilotExtractedTags">) => void;
  onSaveDraftMeasurement: () => Promise<void>;
  onCancelDraftMeasurement: () => void;
  isSavingDraftMeasurement: boolean;
}) {
  const viewport = useDocumentViewport();
  const bounds = getScreenBoundsForGeometry(draft.geometry, viewport);
  const cardWidth = 144;
  const cardLeft = Math.min(
    bounds.x + bounds.width + 10,
    viewport.workspaceSize.width - cardWidth - 12
  );
  const cardHeight = measurementRequiresHeight(draft.tool, draft.measurementType) ? 172 : 142;
  const maxCardTop = Math.max(12, viewport.workspaceSize.height - cardHeight - 12);
  const cardTop = Math.min(Math.max(12, bounds.y), maxCardTop);

  return (
    <div
      className="absolute z-40 pointer-events-auto w-36 rounded-md border bg-background/95 p-2 shadow-xl backdrop-blur"
      style={{ left: cardLeft, top: cardTop }}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="mb-2 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
        {draft.tool} - {draft.measurementType}
      </div>
      <div className="mb-2 text-center">
        <span className="text-lg font-black">
          {formatDisplayedMeasurement(draft.measurementType, draft.value)}
        </span>
        <span className="ml-1 text-sm text-muted-foreground">{draft.measurementType}</span>
      </div>
      {measurementRequiresHeight(draft.tool, draft.measurementType) && (
        <div className="mb-2 text-xs text-muted-foreground">
          Height: {draft.height ? formatMeasurementHeight(draft.height) : "Not set"}
        </div>
      )}
      <Select
        value={draftTagId ? String(draftTagId) : undefined}
        onValueChange={(value) => onDraftTagChange(value as Id<"takeoffPilotExtractedTags">)}
      >
        <SelectTrigger className="mb-2 h-7 w-full bg-background px-2 text-xs">
          <SelectValue placeholder="Assign tag" />
        </SelectTrigger>
        <SelectContent
          className="z-50 max-h-64"
          position="popper"
          sideOffset={4}
          align="start"
          collisionPadding={8}
        >
          {tags.map((tag) => (
            <SelectItem key={tag.id} value={String(tag.id)}>
              {tag.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="flex justify-end gap-1">
        <Button
          size="icon-sm"
          variant="ghost"
          className="h-7 w-7"
          aria-label="Cancel draft measurement"
          onClick={onCancelDraftMeasurement}
        >
          <X className="size-3.5" />
        </Button>
        <Button
          size="icon-sm"
          className="h-7 w-7"
          disabled={!draftTagId || isSavingDraftMeasurement}
          aria-label="Save draft measurement"
          onClick={() => void onSaveDraftMeasurement()}
        >
          <Check className="size-3.5" />
        </Button>
      </div>
    </div>
  );
}

export function SelectedMeasurementCard({
  measurement,
  selectedMeasurements,
  tags,
  scale,
  onSaveMeasurementEdits,
  onUpdateMeasurementAnnotation,
  onApplyMeasurementLeaders,
  onReassignMeasurement,
  onReassignMeasurements,
  isReassigningMeasurementId,
  isDeletingMeasurementId,
  onDeleteMeasurement,
  onDeleteMeasurements,
  onCopyMeasurements,
  onDuplicateMeasurements,
}: {
  measurement: MeasurementOverlayRecord | null;
  selectedMeasurements: MeasurementOverlayRecord[];
  tags: MeasurementTagOption[];
  scale: MeasurementScale | null;
  onSaveMeasurementEdits: (
    measurementId: Id<"takeoffPilotMeasurements">,
    edits: MeasurementEditDraft
  ) => Promise<void>;
  onUpdateMeasurementAnnotation?: (
    measurementId: Id<"takeoffPilotMeasurements">,
    annotation: MeasurementAnnotation
  ) => Promise<void>;
  onApplyMeasurementLeaders?: (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    leaderVisible: boolean
  ) => Promise<boolean>;
  onReassignMeasurement: (
    measurementId: Id<"takeoffPilotMeasurements">,
    tagId: Id<"takeoffPilotExtractedTags">
  ) => Promise<boolean>;
  onReassignMeasurements?: (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    tagId: Id<"takeoffPilotExtractedTags">
  ) => Promise<boolean>;
  isReassigningMeasurementId: Id<"takeoffPilotMeasurements"> | null;
  isDeletingMeasurementId: Id<"takeoffPilotMeasurements"> | null;
  onDeleteMeasurement: (measurementId: Id<"takeoffPilotMeasurements">) => Promise<void>;
  onDeleteMeasurements?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => Promise<void>;
  onCopyMeasurements?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => void;
  onDuplicateMeasurements?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => Promise<void> | void;
}) {
  const [tagDraftId, setTagDraftId] = useState<Id<"takeoffPilotExtractedTags"> | null>(
    () => measurement?.tagId ?? null
  );
  const [valueDraft, setValueDraft] = useState<string>("");
  const [heightDraft, setHeightDraft] = useState<string>("");
  const [leaderVisibleDraft, setLeaderVisibleDraft] = useState(false);
  const [strokeWidthDraft, setStrokeWidthDraft] = useState<string>("");
  const [editError, setEditError] = useState<string | null>(null);
  const [isSavingEdits, setIsSavingEdits] = useState(false);
  const canApplyLeaderUpdates = !!onApplyMeasurementLeaders || !!onUpdateMeasurementAnnotation;

  const applyLeaderVisibility = async (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    leaderVisible: boolean
  ) => {
    if (onApplyMeasurementLeaders) {
      const applied = await onApplyMeasurementLeaders(measurementIds, leaderVisible);
      if (!applied) {
        throw new Error("Failed to update measurement leaders");
      }
      return;
    }
    if (!onUpdateMeasurementAnnotation) {
      throw new Error("Leader updates are unavailable");
    }

    await Promise.all(
      measurementIds.map((measurementId) =>
        onUpdateMeasurementAnnotation(measurementId, {
          leaderVisible,
        })
      )
    );
  };

  useEffect(() => {
    const hasMultiSelection = selectedMeasurements.length > 1;
    const selectedLeadersCount = selectedMeasurements.filter((item) =>
      shouldShowMeasurementLeader(item.annotation)
    ).length;
    const hasMixedLeaders =
      selectedLeadersCount > 0 && selectedLeadersCount < selectedMeasurements.length;
    const selectedLeadersVisible = selectedLeadersCount === selectedMeasurements.length;
    setTagDraftId(hasMultiSelection ? null : (measurement?.tagId ?? null));
    setValueDraft(
      measurement
        ? formatDisplayedMeasurement(measurement.measurementType, measurement.value)
        : ""
    );
    setHeightDraft(measurement?.height ? formatMeasurementHeight(measurement.height) : "");
    setLeaderVisibleDraft(
      hasMultiSelection
        ? hasMixedLeaders
          ? true
          : selectedLeadersVisible
        : shouldShowMeasurementLeader(measurement?.annotation)
    );
    setStrokeWidthDraft(measurement?.annotation?.strokeWidth?.toString() ?? "");
    setEditError(null);
  }, [measurement, selectedMeasurements]);

  if (!measurement) {
    return null;
  }

  if (selectedMeasurements.length > 1) {
    const measurementIds = selectedMeasurements.map((item) => item._id);
    const selectedLeadersCount = selectedMeasurements.filter((item) =>
      shouldShowMeasurementLeader(item.annotation)
    ).length;
    const hasMixedLeaders =
      selectedLeadersCount > 0 && selectedLeadersCount < selectedMeasurements.length;
    const selectedLeadersVisible = selectedLeadersCount === selectedMeasurements.length;
    const leaderChanged = hasMixedLeaders || leaderVisibleDraft !== selectedLeadersVisible;
    return (
      <div
        className="absolute z-40 pointer-events-auto w-44 rounded-md border bg-background/95 p-2 shadow-xl backdrop-blur"
        style={{ right: 16, top: 80 }}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-2 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
          {selectedMeasurements.length} selected
        </div>
        <Select
          value={tagDraftId ? String(tagDraftId) : UNASSIGNED_TAG_VALUE}
          onValueChange={(value) =>
            setTagDraftId(
              value === UNASSIGNED_TAG_VALUE
                ? null
                : (value as Id<"takeoffPilotExtractedTags">)
            )
          }
        >
          <SelectTrigger className="mb-2 h-7 w-full bg-background px-2 text-xs">
            <SelectValue placeholder="Assign tag" />
          </SelectTrigger>
          <SelectContent className="z-[160] max-h-64" position="popper" sideOffset={4} align="start" collisionPadding={8}>
            <SelectItem value={UNASSIGNED_TAG_VALUE} disabled>
              Assign tag
            </SelectItem>
            {tags.map((tag) => (
              <SelectItem key={tag.id} value={String(tag.id)}>
                {tag.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
          <Checkbox
            aria-label="Show leader for selected measurements"
            checked={leaderVisibleDraft}
            onCheckedChange={(checked) => {
              setLeaderVisibleDraft(checked === true);
              if (editError) {
                setEditError(null);
              }
            }}
            disabled={isSavingEdits}
          />
          Show leader
        </label>
        {editError ? (
          <div className="mb-2 text-[11px] leading-tight text-red-500">{editError}</div>
        ) : null}
        <div className="grid grid-cols-2 gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            disabled={isSavingEdits}
            onClick={() => onCopyMeasurements?.(measurementIds)}
          >
            Copy
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            disabled={isSavingEdits}
            onClick={() => void onDuplicateMeasurements?.(measurementIds)}
          >
            Duplicate
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            disabled={!tagDraftId || isSavingEdits}
            onClick={() => {
              if (!tagDraftId) return;
              void onReassignMeasurements?.(measurementIds, tagDraftId);
            }}
          >
            Apply Tag
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            disabled={!leaderChanged || !canApplyLeaderUpdates || isSavingEdits}
            onClick={async () => {
              if (!leaderChanged) {
                return;
              }
              try {
                setIsSavingEdits(true);
                setEditError(null);
                await applyLeaderVisibility(measurementIds, leaderVisibleDraft);
                toast.success(`${measurementIds.length} measurements updated`);
              } catch (error) {
                setEditError(getUserFacingErrorMessage(error, "Failed to update measurement leaders"));
              } finally {
                setIsSavingEdits(false);
              }
            }}
          >
            Apply Leader
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs text-red-500 hover:text-red-500"
            disabled={isSavingEdits}
            onClick={() => void onDeleteMeasurements?.(measurementIds)}
          >
            Delete
          </Button>
        </div>
      </div>
    );
  }

  const requiresHeight = measurementRequiresHeight(
    measurement.tool,
    measurement.measurementType
  );
  const currentValueText = formatDisplayedMeasurement(
    measurement.measurementType,
    measurement.value
  );
  const currentHeightText = measurement.height
    ? formatMeasurementHeight(measurement.height)
    : "";
  const currentStrokeWidth = measurement.annotation?.strokeWidth;
  const valueChanged = valueDraft.trim() !== currentValueText;
  const heightChanged = requiresHeight && heightDraft.trim() !== currentHeightText;
  const tagChanged = tagDraftId !== (measurement.tagId ?? null);
  const leaderChanged = leaderVisibleDraft !== shouldShowMeasurementLeader(measurement.annotation);
  const strokeWidthChanged = strokeWidthDraft !== (currentStrokeWidth?.toString() ?? "");
  const requiresScaleForSave = valueChanged || heightChanged;
  const hasValidScale = (scale?.pixelsPerFoot ?? 0) > 0;
  const hasChanges = valueChanged || heightChanged || tagChanged || leaderChanged || strokeWidthChanged;
  const valueLabel = getEditableValueLabel(measurement.measurementType);

  const saveChanges = async () => {
    try {
      setIsSavingEdits(true);
      setEditError(null);
      if (valueChanged || heightChanged) {
        await onSaveMeasurementEdits(measurement._id, {
          valueInput: valueDraft,
          valueChanged,
          heightInput: heightDraft,
          heightChanged,
        });
      }
      if (tagChanged && tagDraftId) {
        const reassigned = await onReassignMeasurement(measurement._id, tagDraftId);
        if (!reassigned) {
          throw new Error("Failed to update tag");
        }
      }
      if (leaderChanged) {
        await applyLeaderVisibility([measurement._id], leaderVisibleDraft);
      }
      if (strokeWidthChanged) {
        const trimmedStrokeWidth = strokeWidthDraft.trim();
        const annotationUpdates: MeasurementAnnotation = {};
        if (trimmedStrokeWidth.length === 0) {
          annotationUpdates.strokeWidth = undefined;
        } else {
          const parsedStrokeWidth = parseFloat(trimmedStrokeWidth);
          if (!Number.isFinite(parsedStrokeWidth) || parsedStrokeWidth <= 0) {
            setEditError("Stroke width must be a positive number");
            return;
          }
          annotationUpdates.strokeWidth = parsedStrokeWidth;
        }
        await onUpdateMeasurementAnnotation?.(measurement._id, annotationUpdates);
      }
      toast.success("Measurement updated");
    } catch (error) {
      setEditError(getUserFacingErrorMessage(error, "Failed to update measurement"));
    } finally {
      setIsSavingEdits(false);
    }
  };

  return (
    <div
      className="absolute z-40 pointer-events-auto w-36 rounded-md border bg-background/95 p-2 shadow-xl backdrop-blur"
      style={{ right: 16, top: 80 }}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (event.key !== "Enter") {
          return;
        }
        const target = event.target as HTMLElement | null;
        const tagName = target?.tagName ?? "";
        const role = target?.getAttribute("role");
        if (tagName !== "INPUT" || role === "combobox") {
          return;
        }
        if (
          (requiresScaleForSave && !hasValidScale) ||
          !hasChanges ||
          isSavingEdits ||
          isReassigningMeasurementId === measurement._id
        ) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        void saveChanges();
      }}
    >
      <div className="mb-2 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
        {measurement.tool} - {measurement.measurementType}
      </div>
      <div className="mb-2 space-y-2">
        <div>
          <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
            {valueLabel}
          </div>
          <Input
            aria-label="Measurement value"
            value={valueDraft}
            onChange={(event) => {
              setValueDraft(event.target.value);
              if (editError) {
                setEditError(null);
              }
            }}
            disabled={(requiresScaleForSave && !hasValidScale) || isSavingEdits}
            className="h-7 px-2 text-xs"
          />
        </div>
        {requiresHeight ? (
          <div>
            <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
              Height
            </div>
            <Input
              aria-label="Measurement height"
              value={heightDraft}
              onChange={(event) => {
                setHeightDraft(event.target.value);
                if (editError) {
                  setEditError(null);
                }
              }}
              disabled={isSavingEdits}
              className="h-7 px-2 text-xs"
            />
          </div>
        ) : null}
      </div>
      <Select
        value={tagDraftId ? String(tagDraftId) : UNASSIGNED_TAG_VALUE}
        onValueChange={(value) =>
          setTagDraftId(
            value === UNASSIGNED_TAG_VALUE
              ? null
              : (value as Id<"takeoffPilotExtractedTags">)
          )
        }
      >
        <SelectTrigger className="mb-2 h-7 w-full bg-background px-2 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent
          className="z-50 max-h-64"
          position="popper"
          sideOffset={4}
          align="start"
          collisionPadding={8}
        >
          <SelectItem value={UNASSIGNED_TAG_VALUE} disabled>
            Assign tag
          </SelectItem>
          {tags.map((tag) => (
            <SelectItem key={tag.id} value={String(tag.id)}>
              {tag.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <label className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
        <Checkbox
          aria-label="Show measurement leader"
          checked={leaderVisibleDraft}
          onCheckedChange={(checked) => setLeaderVisibleDraft(checked === true)}
        />
        Show leader
      </label>
      <div className="mb-2">
        <div>
          <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
            Stroke width
          </div>
          <Input
            aria-label="Stroke width"
            type="number"
            min={0.5}
            step={0.5}
            value={strokeWidthDraft}
            onChange={(event) => {
              setStrokeWidthDraft(event.target.value);
              if (editError) {
                setEditError(null);
              }
            }}
            disabled={isSavingEdits}
            className="h-7 px-2 text-xs"
            placeholder="2"
          />
        </div>
      </div>
      {editError ? (
        <div className="mb-2 text-[11px] leading-tight text-red-500">{editError}</div>
      ) : null}
      <div className="flex justify-end gap-1">
        <Button
          size="icon-sm"
          variant="ghost"
          className="h-7 w-7"
          disabled={
            isDeletingMeasurementId === measurement._id ||
            isSavingEdits ||
            isReassigningMeasurementId === measurement._id
          }
          aria-label="Delete measurement"
          onClick={(event) => {
            event.stopPropagation();
            void onDeleteMeasurement(measurement._id);
          }}
        >
          <Trash2 className="size-3.5" />
        </Button>
        <Button
          size="icon-sm"
          variant="secondary"
          className="h-7 w-7"
          disabled={
            (requiresScaleForSave && !hasValidScale) ||
            !hasChanges ||
            isSavingEdits ||
            isReassigningMeasurementId === measurement._id
          }
          aria-label="Save measurement changes"
          onClick={async (event) => {
            event.stopPropagation();
            await saveChanges();
          }}
        >
          <Check className="size-3.5" />
        </Button>
      </div>
    </div>
  );
}

export function MeasurementContextMenu({
  x,
  y,
  measurement,
  pagePoint,
  vertexIndex,
  measurements,
  canPasteMeasurement,
  selectedMeasurementIds,
  onClose,
  onCopyMeasurement,
  onCopyMeasurements,
  onPasteMeasurement,
  onDuplicateMeasurement,
  onDuplicateMeasurements,
  onApplyMeasurementLeaders,
  onInsertPoint,
  onRemovePoint,
}: {
  x: number;
  y: number;
  measurement: MeasurementOverlayRecord | null;
  pagePoint: Point | null;
  vertexIndex?: number;
  measurements: MeasurementOverlayRecord[];
  canPasteMeasurement: boolean;
  selectedMeasurementIds: Id<"takeoffPilotMeasurements">[];
  onClose: () => void;
  onCopyMeasurement?: (measurementId: Id<"takeoffPilotMeasurements">) => void;
  onCopyMeasurements?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => void;
  onPasteMeasurement?: () => Promise<void> | void;
  onDuplicateMeasurement?: (measurementId: Id<"takeoffPilotMeasurements">) => Promise<void> | void;
  onDuplicateMeasurements?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => Promise<void> | void;
  onApplyMeasurementLeaders?: (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    leaderVisible: boolean
  ) => Promise<boolean>;
  onInsertPoint?: (
    measurementId: Id<"takeoffPilotMeasurements">,
    point: Point
  ) => Promise<void> | void;
  onRemovePoint?: (
    measurementId: Id<"takeoffPilotMeasurements">,
    vertexIndex: number
  ) => Promise<void> | void;
}) {
  if (!measurement && (!canPasteMeasurement || !onPasteMeasurement)) {
    return null;
  }

  const contextSelectionIds = getContextSelectionIds(measurement, selectedMeasurementIds);
  const selectedIdSet = new Set(contextSelectionIds.map((measurementId) => String(measurementId)));
  const selectedMeasurements = measurements.filter((item) => selectedIdSet.has(String(item._id)));
  const allLeadersVisible =
    selectedMeasurements.length > 0 &&
    selectedMeasurements.every((item) => shouldShowMeasurementLeader(item.annotation));
  const nextLeaderVisible = !allLeadersVisible;
  const leaderActionLabel =
    contextSelectionIds.length > 1
      ? nextLeaderVisible
        ? "Show Leaders for Selected"
        : "Hide Leaders for Selected"
      : nextLeaderVisible
        ? "Show Leader"
        : "Hide Leader";
  const canInsertPoint =
    !!onInsertPoint &&
    !!pagePoint &&
    canInsertPointOnMeasurement(measurement);
  const canRemovePoint =
    !!onRemovePoint &&
    vertexIndex !== undefined &&
    canRemovePointFromMeasurement(measurement);

  return (
    <div
      data-measurement-context-menu="true"
      className="absolute z-50 min-w-28 max-w-44 rounded-md border bg-background/95 p-0.5 shadow-xl backdrop-blur"
      style={{ left: x + 6, top: y + 6 }}
      onMouseDown={(event) => event.stopPropagation()}
    >
      {measurement && onCopyMeasurement ? (
        <button
          type="button"
          className={CONTEXT_MENU_ITEM_CLASS}
          onClick={() => {
            onCopyMeasurement(measurement._id);
            onClose();
          }}
        >
          Copy
        </button>
      ) : null}
      {measurement && onCopyMeasurements && contextSelectionIds.length > 1 ? (
        <button
          type="button"
          className={CONTEXT_MENU_ITEM_CLASS}
          onClick={() => {
            onCopyMeasurements(contextSelectionIds);
            onClose();
          }}
        >
          Copy Selected
        </button>
      ) : null}
      {measurement && onDuplicateMeasurement ? (
        <button
          type="button"
          className={CONTEXT_MENU_ITEM_CLASS}
          onClick={() => {
            void onDuplicateMeasurement(measurement._id);
            onClose();
          }}
        >
          Duplicate
        </button>
      ) : null}
      {measurement && onDuplicateMeasurements && contextSelectionIds.length > 1 ? (
        <button
          type="button"
          className={CONTEXT_MENU_ITEM_CLASS}
          onClick={() => {
            void onDuplicateMeasurements(contextSelectionIds);
            onClose();
          }}
        >
          Duplicate Selected
        </button>
      ) : null}
      {measurement && onApplyMeasurementLeaders ? (
        <button
          type="button"
          className={CONTEXT_MENU_ITEM_CLASS}
          onClick={async () => {
            onClose();
            try {
              const applied = await onApplyMeasurementLeaders(
                contextSelectionIds,
                nextLeaderVisible
              );
              if (!applied) {
                toast.error("Failed to update measurement leaders");
                return;
              }
              toast.success(
                contextSelectionIds.length > 1
                  ? `${contextSelectionIds.length} measurements updated`
                  : "Measurement updated"
              );
            } catch (error) {
              console.error(error);
              toast.error("Failed to update measurement leaders");
            }
          }}
        >
          {leaderActionLabel}
        </button>
      ) : null}
      {canInsertPoint ? (
        <button
          type="button"
          className={CONTEXT_MENU_ITEM_CLASS}
          onClick={async () => {
            if (!pagePoint || !measurement) {
              return;
            }
            try {
              await onInsertPoint(measurement._id, pagePoint);
              onClose();
            } catch (error) {
              toast.error(getUserFacingErrorMessage(error, "Failed to insert point"));
            }
          }}
        >
          Insert Point
        </button>
      ) : null}
      {canRemovePoint ? (
        <button
          type="button"
          className={`${CONTEXT_MENU_ITEM_CLASS} text-red-600`}
          onClick={async () => {
            if (vertexIndex === undefined || !measurement) {
              return;
            }
            try {
              await onRemovePoint(measurement._id, vertexIndex);
              onClose();
            } catch (error) {
              toast.error(getUserFacingErrorMessage(error, "Failed to remove point"));
            }
          }}
        >
          Remove Point
        </button>
      ) : null}
      {canPasteMeasurement && onPasteMeasurement ? (
        <button
          type="button"
          className={CONTEXT_MENU_ITEM_CLASS}
          onClick={() => {
            void onPasteMeasurement();
            onClose();
          }}
        >
          Paste
        </button>
      ) : null}
    </div>
  );
}
